import { inject, Injectable, LOCALE_ID } from "@angular/core";

import { AppService } from "../app.service";
import { ICapabilityForm } from "@in2/models";

export type CapabilityViewModel = Partial<ICapabilityForm>;

@Injectable({ providedIn: "root" })
export class CapabilitiesService {
    private readonly capabilities: Partial<CapabilityViewModel>[] = [];
    private readonly contentService = inject(AppService);
    private readonly locale = inject(LOCALE_ID);
    list(excludeItems?: string[]): Promise<Partial<CapabilityViewModel>[]> {
        const q = {
            lang: this.locale,
            status: "published",
            select: "status,slug,title,image",
        };
        const slugs = excludeItems?.map((s) => s.trim()).filter((s) => s.length > 0);
        if (slugs?.length) q["slug"] = `{nin}${slugs.join(",")}`;
        return this.contentService.fetchJsonAsset<ICapabilityForm[]>(`capability`, q).then((res) => res.data ?? []);
    }

    async cards(_slugs?: string[]): Promise<ICapabilityForm[]> {
        const css = await this.getCapabilities();

        const slugs = (_slugs || []).length > 0 ? _slugs : css.map((c) => c.slug!);
        const capabilities = css.filter((c) => slugs?.some((s) => s === c.slug));

        return capabilities as ICapabilityForm[];
    }

    async getCapabilities(excludeBySlug?: string): Promise<Partial<CapabilityViewModel>[]> {
        let cs = this.capabilities.slice();
        if (cs.length < 1) cs = await this.list();
        return cs.filter((r) => (excludeBySlug ? excludeBySlug !== r.slug : r));
    }
    getCapability(slug: string): Promise<CapabilityViewModel> {
        const loaded = this.capabilities.find((c) => c.slug === slug);
        if (loaded?.body) return Promise.resolve(loaded as CapabilityViewModel);
        const tasks = [Promise.resolve(loaded), this.getCapabilityBody(slug)];
        return Promise.allSettled(tasks).then(([loaded, body]) => {
            let res = {} as CapabilityViewModel;
            if (loaded.status === "fulfilled") res = { ...res, ...(loaded.value as CapabilityViewModel) };
            if (body.status === "fulfilled") res = { ...res, body: body.value as string };
            return res;
        });
    }

    async getCapabilityBody(slug: string): Promise<string> {
        const loadedIdx = this.capabilities.findIndex((c) => c.slug === slug);
        const loaded = loadedIdx > -1 ? this.capabilities[loadedIdx] : null;
        const tasks: [Promise<Partial<CapabilityViewModel>>, Promise<string>] = [
            this.contentService.fetchJsonAsset<Partial<CapabilityViewModel>>(`/capability?lang=${this.locale}&slug=${slug}&select=body`).then((res) => res.data?.[0]),
            this.contentService.fetchCapabilityBody(slug),
        ];

        if (loaded) tasks[0] = Promise.resolve(loaded);
        if (loaded?.body) tasks[1] = Promise.resolve(loaded.body);

        try {
            const resolved = await Promise.allSettled(tasks).then(([data, body]) => {
                let cs = {} as CapabilityViewModel;
                if (data.status === "fulfilled") cs = data.value as CapabilityViewModel;
                if (body.status === "fulfilled") cs["body"] = body.value;
                return cs;
            });
            if (loadedIdx > -1) this.capabilities[loadedIdx] = { ...loaded, ...resolved };
            else this.capabilities.push(resolved);

            return resolved.body || "";
        } catch (error) {
            console.error("error", error);
            throw "NO_CASE_STUDY_FOUND";
        }
    }
}
